import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Activity, CloudSun, Loader2, RefreshCw } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";

const when = (iso) => (iso ? new Date(iso).toLocaleString() : "never");

/** Platform-owner master control. The switch is enforced in the backend — hiding this panel is
 *  not the protection; a 403 is. */
export function WeatherControl() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/platform/weather").then(({ data }) => setState(data)).catch(() => setState(null));
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const toggle = async (on) => {
    setBusy(true);
    try {
      await api.post("/platform/weather", { enabled: on });
      toast.success(on ? "Weather intelligence is ON" : "Weather intelligence is OFF");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not change the setting");
    }
    setBusy(false);
  };

  const refresh = async () => {
    setBusy(true);
    try {
      await api.post("/platform/weather/refresh");
      toast.success("Refreshing every property from NWS and NHC");
      setTimeout(load, 8000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Refresh failed");
    }
    setBusy(false);
  };

  if (!state) return null;

  return (
    <section data-testid="weather-control" className="rounded-xl border border-border bg-card p-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-royal-deep">
            <CloudSun className="h-4 w-4 text-royal" /> Weather Intelligence System
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Official National Weather Service and National Hurricane Center data, platform-wide.
            Only you can switch this on or off.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={refresh} disabled={busy}
                  data-testid="weather-force-refresh-btn" className="gap-2">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Refresh now
          </Button>
          <span className={`text-xs font-semibold uppercase tracking-wider ${state.enabled ? "text-emerald-700" : "text-muted-foreground"}`}>
            {state.enabled ? "On" : "Off"}
          </span>
          <Switch checked={state.enabled} onCheckedChange={toggle} disabled={busy}
                  data-testid="weather-master-switch" />
        </div>
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Cell label="Last NWS update" value={when(state.last_nws_update)} />
        <Cell label="Last NHC update" value={when(state.last_nhc_update)}
              hint={`${state.active_storms} active storm(s) · ${state.nhc_status || "unknown"}`} />
        <Cell label="Last AI weather briefing" value={when(state.last_briefing_update)} />
        <Cell label="Properties tracked" value={state.properties_tracked}
              hint={`${state.properties_stale} stale/unavailable · ${state.geocode_failures_stored} without coordinates`} />
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3" data-testid="weather-source-health">
        {(state.sources || []).map((s) => (
          <div key={s.source} className={`rounded-lg border px-3.5 py-2.5 text-xs ${s.circuit_open ? "border-red-300 bg-red-50" : s.errors ? "border-amber-300 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`}>
            <p className="flex items-center gap-1.5 font-semibold uppercase tracking-wider text-royal-deep">
              <Activity className="h-3 w-3" /> {s.source}
            </p>
            <p className="mt-1 text-muted-foreground">
              {s.circuit_open ? "Circuit open — backing off" : s.errors ? "Recovering" : "Healthy"}
              {" · "}{s.calls} calls, {s.error_rate_pct}% errors
              {s.avg_ms ? `, ${s.avg_ms} ms avg` : ""}
            </p>
            <p className="text-muted-foreground">Last success {when(s.last_success)}</p>
            {s.rate_limit_events ? <p className="text-amber-700">{s.rate_limit_events} rate-limit event(s)</p> : null}
            {s.last_error ? <p className="mt-1 text-destructive line-clamp-2">{s.last_error}</p> : null}
          </div>
        ))}
      </div>

      <p className="mt-3 text-[11px] text-muted-foreground">
        Last changed {when(state.updated_at)}{state.updated_by ? ` by ${state.updated_by}` : ""}
        {state.counters?.unauthorized_attempts
          ? ` · ${state.counters.unauthorized_attempts} unauthorized attempt(s) blocked and logged`
          : ""}
      </p>
    </section>
  );
}

function Cell({ label, value, hint }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-sm font-medium text-royal-deep">{value}</p>
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

export default WeatherControl;
